import React, { useEffect, useRef, useState } from 'react';

const fogPalettes = {
  regular: {
    highlightColor: 0xc6d0ed,
    midtoneColor: 0xffffff,
    lowlightColor: 0xe3e2f2,
    baseColor: 0xffebeb,
    blurFactor: 0.55,
    speed: 1.25,
    zoom: 2.6,
  },
  day: {
    highlightColor: 0xd5e8f8,
    midtoneColor: 0xffffff,
    lowlightColor: 0xc3d9e9,
    baseColor: 0xf4f9fb,
    blurFactor: 0.52,
    speed: 1.05,
    zoom: 2.6,
  },
  dusk: {
    highlightColor: 0xffddbc,
    midtoneColor: 0xfff1df,
    lowlightColor: 0xd8a9a6,
    baseColor: 0xfbe4d7,
    blurFactor: 0.56,
    speed: 0.85,
    zoom: 2.7,
  },
  night: {
    highlightColor: 0x778ba7,
    midtoneColor: 0x414d66,
    lowlightColor: 0x171d2b,
    baseColor: 0x262f40,
    blurFactor: 0.59,
    speed: 0.58,
    zoom: 2.8,
  },
  orbital: {
    highlightColor: 0xe0e9ed, midtoneColor: 0xf9fbfa, lowlightColor: 0xd3dfdf,
    baseColor: 0xeef3f1, blurFactor: 0.62, speed: 0.35, zoom: 2.9,
  },
  atelier: {
    highlightColor: 0xe5d7bd, midtoneColor: 0xfaf5e9, lowlightColor: 0xe6d8c5,
    baseColor: 0xf4eddf, blurFactor: 0.65, speed: 0.3, zoom: 3.4,
  },
};

const canUseWebGL = () => {
  try {
    const canvas = document.createElement('canvas');
    const context = window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    // Capability probes should not retain extra GPU contexts between visits.
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    return Boolean(context);
  } catch {
    return false;
  }
};

const shouldUseAnimatedBackground = () => {
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  const smallScreen = window.matchMedia?.('(max-width: 768px)').matches;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const saveData = Boolean(connection?.saveData);
  const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
  const lowConcurrency = typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;

  return !reducedMotion && !coarsePointer && !smallScreen && !saveData && !lowMemory && !lowConcurrency && canUseWebGL();
};

const VantaBackground = ({ skin = 'regular', period = 'day' }) => {
  const effectRef = useRef(null);
  const elementRef = useRef(null);
  const paletteRef = useRef(fogPalettes.regular);
  const [animated, setAnimated] = useState(false);
  const isDesktopBackground = skin === 'terminal';

  paletteRef.current = skin === 'dynamic' ? fogPalettes[period] || fogPalettes.day : fogPalettes[skin] || fogPalettes.regular;

  useEffect(() => {
    effectRef.current?.setOptions(paletteRef.current);
  }, [skin, period]);

  useEffect(() => {
    // The macOS reading environment uses a static desktop wallpaper. Changing
    // to it also runs the previous effect's cleanup, releasing the WebGL canvas.
    if (isDesktopBackground) return undefined;

    let cancelled = false;
    let initializing = false;
    let idleHandle = null;
    let fallbackTimer = null;

    const cancelScheduledEffect = () => {
      if (idleHandle !== null) window.cancelIdleCallback?.(idleHandle);
      window.clearTimeout(fallbackTimer);
      idleHandle = null;
      fallbackTimer = null;
    };

    const destroyEffect = () => {
      if (effectRef.current) {
        effectRef.current.destroy();
        effectRef.current = null;
      }
      setAnimated(false);
    };

    const initializeEffect = async () => {
      if (
        cancelled ||
        document.hidden ||
        initializing ||
        effectRef.current ||
        !elementRef.current ||
        !shouldUseAnimatedBackground()
      ) {
        return;
      }

      initializing = true;
      try {
        // WebGL 依赖只在性能合适的桌面设备上下载，移动端直接使用静态渐变。
        const [{ default: FOG }, THREE] = await Promise.all([
          import('vanta/dist/vanta.fog.min'),
          import('three'),
        ]);

        if (cancelled || document.hidden || !elementRef.current || !shouldUseAnimatedBackground()) return;

        const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        effectRef.current = FOG({
          el: elementRef.current,
          THREE,
          mouseControls: true,
          touchControls: false,
          gyroControls: false,
          minHeight: 200,
          minWidth: 200,
          scale: Math.max(1, devicePixelRatio / 1.25),
          scaleMobile: 2,
          ...paletteRef.current,
        });
        setAnimated(Boolean(effectRef.current));
      } catch (error) {
        // 静态 CSS 背景始终存在，因此 WebGL 失败时页面仍能正常显示。
        console.warn('动态背景不可用，已切换为静态背景。', error);
        destroyEffect();
      } finally {
        initializing = false;
      }
    };

    const scheduleEffect = () => {
      cancelScheduledEffect();
      if (cancelled || document.hidden || effectRef.current) return;
      // Keep WebGL's large dependencies and shader compilation off the first
      // content render. The static gradient already supplies a complete frame.
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(initializeEffect, { timeout: 1800 });
      } else {
        fallbackTimer = window.setTimeout(initializeEffect, 600);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelScheduledEffect();
        destroyEffect();
      } else {
        scheduleEffect();
      }
    };

    if (document.readyState === 'complete') scheduleEffect();
    else window.addEventListener('load', scheduleEffect, { once: true });
    // Some browsers expose WebGL shortly after first paint; retry once before
    // settling on the static sky for this visit.
    const startupRetry = window.setTimeout(scheduleEffect, 2800);
    const motionQueries = ['(prefers-reduced-motion: reduce)', '(pointer: coarse)', '(max-width: 768px)']
      .map(query => window.matchMedia(query));
    const handleCapabilityChange = () => {
      if (motionQueries.some(query => query.matches)) {
        cancelScheduledEffect();
        destroyEffect();
      } else scheduleEffect();
    };
    motionQueries.forEach(query => query.addEventListener?.('change', handleCapabilityChange));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', scheduleEffect);

    return () => {
      cancelled = true;
      cancelScheduledEffect();
      window.clearTimeout(startupRetry);
      motionQueries.forEach(query => query.removeEventListener?.('change', handleCapabilityChange));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', scheduleEffect);
      window.removeEventListener('load', scheduleEffect);
      destroyEffect();
    };
  }, [isDesktopBackground]);

  return (
    <div
      ref={elementRef}
      className={`site-background${isDesktopBackground ? ' site-background--desktop' : animated ? ' site-background--animated' : ''}`}
      aria-hidden="true"
    />
  );
};

export default VantaBackground;
